import React from 'react'
import HomeCard from './HomeCard'
import FantasyTeamPerformanceReport from 'components/fantasy-team-performance-report/FantasyTeamPerformanceReport'
import LiveGameScores from 'components/live-game-scores/LiveGameScores'
import type ReactGridLayout from 'react-grid-layout'
import { Responsive, WidthProvider } from 'react-grid-layout'
import TopFantasyPlayers from './TopFantasyPlayers'
import WeeklyFantasyTeamPerformanceDashboard from './WeeklyFantasyTeamPerformanceDashboard'
import useFantaHockeyStore from 'hooks/store/FantaHockeyStateStore'
import MobileSwipeHelp from './MobileSwipeHelp'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Box } from '@mui/material'
import { isMobile } from 'react-device-detect'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'

interface Props {
  isEditing: boolean
}

const HomeGridLayout: React.FC<Props> = ({ isEditing }) => {
  const store = useFantaHockeyStore()
  if (isMobile && !isEditing) {
    return (
      <Box>
        <Swiper
          className="swipper-wrapper"
          modules={[Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          simulateTouch={true}
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
        >

          <SwiperSlide>
            <Box m={2} height={'calc(100dvh - 150px)'}>
              <HomeCard title="Top Fantasy Players" minHeight={'70vh'} overflowY={store.isConnectedToYahooFantasy ? 'scroll' : 'hidden'}>
                <TopFantasyPlayers isEditing={!isEditing} />
              </HomeCard>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box m={2} height={'calc(100dvh - 150px)'}>
              <HomeCard title="Weekly Team Performance" minHeight={'70dvh'} overflowY='hidden'>
                <WeeklyFantasyTeamPerformanceDashboard isEditing={!isEditing} />
              </HomeCard>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box m={2} height={'calc(100dvh - 150px)'}>
            <HomeCard
              title="Fantasy Team Report"
              overflowY={store.isConnectedToYahooFantasy ? 'scroll' : 'hidden'}
              minHeight={'70dvh'}
            >
              <FantasyTeamPerformanceReport isEditing={!isEditing} />
              </HomeCard>
            </Box>
          </SwiperSlide>
          <SwiperSlide>
            <Box m={2} height={'calc(100dvh - 150px)'}>
              <HomeCard title="Live Scores" minHeight={'70dvh'}>
                <LiveGameScores numberOfGames={3} setTitle={false} setIcon={false} isEditing={!isEditing} />
              </HomeCard>
            </Box>
          </SwiperSlide>
        </Swiper>
      <MobileSwipeHelp isMobile={isMobile} isEditing={isEditing} />
      </Box>
    )
  }

  const [homeLayout, setHomeLayout] = useLocalStorage<string | undefined>('fantaHockey:homeGridLayout', undefined)
  const defaultLayout = {
    lg: [
      { i: 'fantasy-team-report', x: 0, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'top-fantasy-players', x: 1, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'weekly-team-performance', x: 2, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'live-scores', x: 3, y: 0, w: 3, h: 7, minH: 5 }
    ],
    md: [
      { i: 'fantasy-team-report', x: 0, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'top-fantasy-players', x: 1, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'weekly-team-performance', x: 2, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'live-scores', x: 3, y: 0, w: 3, h: 7, minH: 7 }
    ],
    sm: [
      { i: 'fantasy-team-report', x: 0, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'top-fantasy-players', x: 0, y: 5, w: 1, h: 5, minH: 5 },
      { i: 'weekly-team-performance', x: 0, y: 10, w: 1, h: 5, minH: 5 },
      { i: 'live-scores', x: 3, y: 15, w: 1, h: 7, minH: 7 }
    ],
    xs: [
      { i: 'fantasy-team-report', x: 0, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'top-fantasy-players', x: 0, y: 5, w: 1, h: 5, minH: 5 },
      { i: 'weekly-team-performance', x: 0, y: 10, w: 1, h: 5, minH: 5 },
      { i: 'live-scores', x: 3, y: 15, w: 1, h: 7, minH: 7 }
    ],
    xxs: [
      { i: 'fantasy-team-report', x: 0, y: 0, w: 1, h: 5, minH: 5 },
      { i: 'top-fantasy-players', x: 0, y: 5, w: 1, h: 5, minH: 5 },
      { i: 'weekly-team-performance', x: 0, y: 10, w: 1, h: 5, minH: 5 },
      { i: 'live-scores', x: 3, y: 15, w: 1, h: 7, minH: 7 }
    ]
  }

  const layoutChangeHandler = React.useCallback((layout: ReactGridLayout.Layout[], layouts: ReactGridLayout.Layouts) => {
    if (JSON.stringify(layouts) === JSON.stringify(layout)) return
    setHomeLayout(JSON.stringify(layouts))
  }, [setHomeLayout])

  const ResponsiveReactGridLayout = WidthProvider(Responsive)
  return (
    <Box m={1}>
      <ResponsiveReactGridLayout
        className="layout"
        isResizable={false}
        cols={{ lg: 3, md: 3, sm: 3, xs: 1, xxs: 1 }}
        rowHeight={80}
        margin={[16, 16]}
        containerPadding={[16, 16]}
        preventCollision={false}
        compactType={null}
        isDraggable={isEditing}
        resizeHandles={[]}
        useCSSTransforms={false}
        layouts={homeLayout !== undefined ? JSON.parse(homeLayout) : defaultLayout}
        onLayoutChange={isEditing
          ? layoutChangeHandler
          : undefined}>
        <Box key="fantasy-team-report">
          <HomeCard
            title="Fantasy Team Report"
            overflowY={store.isConnectedToYahooFantasy ? 'scroll' : 'hidden'}
            minHeight={425} maxHeight={425}
          >
            <FantasyTeamPerformanceReport isEditing={!isEditing} />
          </HomeCard>
        </Box>
        <Box key="top-fantasy-players">
          <HomeCard title="Top Fantasy Players" minHeight={425} maxHeight={425} overflowY={store.isConnectedToYahooFantasy ? 'scroll' : 'hidden'}>
            <TopFantasyPlayers isEditing={!isEditing} />
          </HomeCard>
        </Box>
        <Box key="weekly-team-performance">
          <HomeCard title="Team Performance" minHeight={425} maxHeight={425} overflowY='hidden'>
            <WeeklyFantasyTeamPerformanceDashboard isEditing={!isEditing} />
          </HomeCard>
        </Box>
        <Box key="live-scores">
          <HomeCard key={'live-scores'} title="Live Scores" minHeight={385} maxHeight={500}>
            <LiveGameScores numberOfGames={3} setTitle={false} setIcon={false} isEditing={!isEditing} />
          </HomeCard>
        </Box>
      </ResponsiveReactGridLayout>
    </Box>
  )
}

export default React.memo(HomeGridLayout)
